import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { HelperService, PeaksRequestStatus, UserService, WaveformPeaks, environment, parsePeaksFile, peaksUrlFor, resolveWaveformPeaks } from "@mypxplat/xplat/core";

/**
 * Waveform peaks for the web player (DENG-905): the envelope WaveSurfer draws, fetched from
 * `<audio url>.peaks.json` so the browser never downloads and decodes a whole file to draw one.
 * When the file doesn't exist yet it asks paeapi to generate it and waits for it (see
 * resolveWaveformPeaks). A rejection means "draw it the old way": the caller falls back to
 * letting WaveSurfer decode the audio.
 */
@Injectable({ providedIn: "root" })
export class WaveformPeaksService {
  private _memory = new Map<string, WaveformPeaks>();
  /** In flight, so two players on the same file share one request and one poll. */
  private _pending = new Map<string, Promise<WaveformPeaks>>();

  constructor(
    private _http: HttpClient,
    private _helper: HelperService,
    private _user: UserService
  ) {}

  load(file: { id: any; url: string }, isCancelled?: () => boolean): Promise<WaveformPeaks> {
    const key = `${file.id}`;
    const ready = this._memory.get(key);
    if (ready) return Promise.resolve(ready);
    const pending = this._pending.get(key);
    if (pending) return pending;

    const load = resolveWaveformPeaks({
      fetchPeaks: () => this._fetch(file.url),
      requestGeneration: () => this._request(file.id),
      isCancelled,
    })
      .then((peaks) => {
        this._memory.set(key, peaks);
        return peaks;
      })
      .finally(() => this._pending.delete(key));

    this._pending.set(key, load);
    return load;
  }

  /**
   * One look at the peaks object. A plain fetch rather than HttpClient, so the auth interceptor
   * doesn't put a user token on a request to the public CDN. `no-cache` so a poll always asks
   * the CDN again instead of reusing an earlier 404.
   */
  private async _fetch(audioUrl: string): Promise<WaveformPeaks | null> {
    try {
      const response = await fetch(peaksUrlFor(audioUrl), { cache: "no-cache" });
      if (!response.ok) return null;
      return parsePeaksFile(await response.json());
    } catch {
      return null;
    }
  }

  /**
   * Asks paeapi to generate the file. Signed-out viewers (a public share link) can't: the
   * endpoint needs a user, so they go straight to the decoding fallback.
   */
  private async _request(fileId: any): Promise<PeaksRequestStatus> {
    if (!fileId || !this._user.user) return null;
    try {
      const body: any = await firstValueFrom(this._http.post(`${environment.paeApiUrl}content/file/${fileId}/peaks`, JSON.stringify({}), this._helper.getHttpOptions()));
      return body?.status || null;
    } catch {
      return null;
    }
  }
}
